import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/pageTitle';
import Button from 'components/ui/Button';
import {
  Home,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Search,
  BookOpen,
  CheckSquare,
  ListTodo,
  Users,
  BarChart3,
  RefreshCw,
  Download,
  Filter,
  X,
  ArrowRight,
  TrendingUp,
  Calendar,
  Star,
  Activity,
  PieChart,
  Briefcase
} from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { useToast } from 'contexts/ToastContext';
import {
  getLegalResearchRequests
} from 'services/legalResearchService';
import {
  getLegislativeDraftings
} from 'services/legislativeService';
import {
  getLegalAdvisories
} from 'services/legalAdvisoryService';
import {
  getSectorDirectives
} from 'services/sectorDirectives';
import {
  getRegisterDirectives
} from 'services/registerDirectivesService';
import {
  getLegalTrainingProvisions
} from 'services/legalTrainingProvision';
import {
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGISLATIVE_DRAFTING_REQUEST_STATUS,
  LEGAL_ADVISORY_REQUEST_STATUS,
  SECTOR_DIRECTIVES_REQUEST_STATUS,
  LEGAL_TRAINING_PROVISION_REQUEST_STATUS
} from 'utils/constantValues';

// ==================== TYPES ====================

interface StatusDefinition {
  label: string;
  value: string;
}

type ServiceKey = 'legalResearch' | 'legislativeDrafting' | 'legalAdvisory' | 'sectorDirectives' | 'registerDirectives' | 'legalTraining';

interface ServiceData<T> {
  data: T[];
  loading: boolean;
}

interface FilterState {
  dateRange: 'today' | 'week' | 'month' | 'year' | 'custom';
  startDate: string;
  endDate: string;
  services: ServiceKey[];
  status: 'all' | 'active' | 'completed' | 'pendingReview';
  search: string;
}

interface ServiceConfig {
  key: ServiceKey;
  title: string;
  description: string;
  icon: React.ReactNode;
  iconColor: string;
  bgColor: string;
  borderColor: string;
  badgeColor: string;
}

interface StatsCard {
  title: string;
  value: number;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}

// ==================== CONFIGURATION ====================

const SERVICE_CONFIGS: ServiceConfig[] = [
  {
    key: 'legalResearch',
    title: 'ConductLegalResearch',
    description: 'LegalResearchRequestTypes',
    icon: <Search className="w-5 h-5" />,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-100',
    badgeColor: 'bg-blue-100 text-blue-700'
  },
  {
    key: 'legislativeDrafting',
    title: 'LegislativeDrafting',
    description: 'DraftLegislationRequests',
    icon: <FileText className="w-5 h-5" />,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-100',
    badgeColor: 'bg-purple-100 text-purple-700'
  },
  {
    key: 'legalAdvisory',
    title: 'LegalAdvisory',
    description: 'LegalAdviceRequests',
    icon: <Users className="w-5 h-5" />,
    iconColor: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-100',
    badgeColor: 'bg-teal-100 text-teal-700'
  },
  {
    key: 'sectorDirectives',
    title: 'CheckingAccuracyOfSectorDirectives',
    description: 'SectorDirectiveReviews',
    icon: <CheckSquare className="w-5 h-5" />,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-100',
    badgeColor: 'bg-amber-100 text-amber-700'
  },
  {
    key: 'registerDirectives',
    title: 'RegisterDirective',
    description: 'DirectiveRegistrationRequests',
    icon: <ListTodo className="w-5 h-5" />,
    iconColor: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-100',
    badgeColor: 'bg-slate-100 text-slate-700'
  },
  {
    key: 'legalTraining',
    title: 'LegalTrainingProvision',
    description: 'TrainingPrograms',
    icon: <BookOpen className="w-5 h-5" />,
    iconColor: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-100',
    badgeColor: 'bg-rose-100 text-rose-700'
  }
];

const getStatusMap = (service: ServiceKey): Record<string, StatusDefinition> => {
  switch (service) {
    case 'legalResearch': return LEGAL_RESEARCH_REQUEST_STATUS;
    case 'legislativeDrafting': return LEGISLATIVE_DRAFTING_REQUEST_STATUS;
    case 'legalAdvisory': return LEGAL_ADVISORY_REQUEST_STATUS;
    case 'sectorDirectives': return SECTOR_DIRECTIVES_REQUEST_STATUS;
    case 'registerDirectives': return SECTOR_DIRECTIVES_REQUEST_STATUS;
    case 'legalTraining': return LEGAL_TRAINING_PROVISION_REQUEST_STATUS;
  }
};

const STATUS_KEYWORDS: Record<string, string[]> = {
  active: [
    'received', 'pending', 'underway', 'in progress', 'under review',
    'under analysis', 'submitted', 'submitted by stakeholder', 'submitted internally',
    'oral instruction logged', 'proposal submitted', 'proposal underway',
    'draft prepared', 'team appointed', 'identification in progress',
    'audit in progress', 'summary submitted', 'session scheduled',
    'team assigned', 'training manual submitted', 'training scheduled',
    'assigned to prosecutor', 'forwarded to', 'sent to'
  ],
  completed: [
    'completed', 'published', 'final', 'archived', 'closed',
    'finalization complete', 'audit complete', 'final approval',
    'reference number submitted', 'letter reference number submitted'
  ],
  pendingReview: [
    'revision', 'revision requested', 'revision updated', 'feedback',
    'awaiting approval', 'draft approved', 'proposal revision',
    'proposal update', 'draft - updated', 'proposal presentation feedback submitted',
    'additional info', 'waiting for', 'back to', 'reviewed by',
    'approved by head', 'follow-up'
  ]
};

// ==================== UTILITIES ====================

const getDateRange = (range: 'today' | 'week' | 'month' | 'year' | 'custom', start?: string, end?: string) => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (range) {
    case 'today':
      return { start: startOfToday, end: new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000) };
    case 'week':
      const weekStart = new Date(startOfToday);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      return { start: weekStart, end: new Date() };
    case 'month':
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: new Date() };
    case 'year':
      return { start: new Date(now.getFullYear(), 0, 1), end: new Date() };
    case 'custom':
      return { start: start ? new Date(start) : new Date(0), end: end ? new Date(end) : new Date() };
    default:
      return { start: new Date(0), end: new Date() };
  }
};

const filterByDate = <T,>(items: T[], start: Date, end: Date): T[] => {
  return items.filter(item => {
    const created_at = (item as any).created_at || (item as any).createdDate;
    if (!created_at) return false;
    const itemDate = new Date(created_at);
    return itemDate >= start && itemDate <= end;
  });
};

const filterBySearch = <T,>(items: T[], search: string, titleFields: string[]): T[] => {
  if (!search.trim()) return items;
  const lowerSearch = search.toLowerCase();
  return items.filter(item =>
    titleFields.some(field => {
      const value = (item as any)[field];
      return value && String(value).toLowerCase().includes(lowerSearch);
    })
  );
};

const categorizeStatus = (label: string): 'active' | 'completed' | 'pendingReview' | 'other' => {
  const lowerLabel = label.toLowerCase();
  for (const [category, keywords] of Object.entries(STATUS_KEYWORDS)) {
    if (keywords.some(keyword => lowerLabel.includes(keyword))) {
      return category as 'active' | 'completed' | 'pendingReview';
    }
  }
  return 'other';
};

const calculateStats = (items: any[], statusMap: Record<string, StatusDefinition>): { active: number; completed: number; pendingReview: number } => {
  let active = 0, completed = 0, pendingReview = 0;

  items.forEach(item => {
    const statusValue = item.status || item.current_status || item.request_status;
    const statusDef = Object.values(statusMap).find(s => s.value === statusValue);
    if (statusDef) {
      switch (categorizeStatus(statusDef.label)) {
        case 'active': active++; break;
        case 'completed': completed++; break;
        case 'pendingReview': pendingReview++; break;
      }
    }
  });

  return { active, completed, pendingReview };
};

const calculateChange = (current: number, previous: number): string => {
  if (previous === 0) return current > 0 ? '+100%' : '0%';
  const change = ((current - previous) / previous) * 100;
  return `${change >= 0 ? '+' : ''}${change.toFixed(0)}%`;
};

const exportToCSV = (allData: Record<ServiceKey, ServiceData<any>>, statusCounts: Record<ServiceKey, Record<string, number>>, t: (key: string) => string) => {
  const headers = ['Service', 'Total', 'Active', 'Completed', 'Pending Review', 'Status Breakdown'];
  const rows = SERVICE_CONFIGS.map(config => {
    const total = allData[config.key].data.length;
    const statusMap = getStatusMap(config.key);
    const stats = calculateStats(allData[config.key].data, statusMap);
    const breakdown = Object.entries(statusCounts[config.key] || {})
      .map(([status, count]) => `${status}: ${count}`)
      .join('; ');
    return [t(config.title), total, stats.active, stats.completed, stats.pendingReview, breakdown];
  });

  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `dashboard-export-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

// ==================== MAIN COMPONENT ====================

const LegalResearchCoordinatorDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();

  // Data state
  const [allServiceData, setAllServiceData] = useState<Record<ServiceKey, ServiceData<any>>>({
    legalResearch: { data: [], loading: true },
    legislativeDrafting: { data: [], loading: true },
    legalAdvisory: { data: [], loading: true },
    sectorDirectives: { data: [], loading: true },
    registerDirectives: { data: [], loading: true },
    legalTraining: { data: [], loading: true }
  });

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    dateRange: 'month',
    startDate: '',
    endDate: '',
    services: [],
    status: 'all',
    search: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get current date for welcome message
  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [
        researchData,
        draftingData,
        advisoryData,
        sectorData,
        registerData,
        trainingData
      ] = await Promise.allSettled([
        getLegalResearchRequests(''),
        getLegislativeDraftings(),
        getLegalAdvisories(),
        getSectorDirectives(),
        getRegisterDirectives(''),
        getLegalTrainingProvisions()
      ]);

      setAllServiceData(prev => ({
        legalResearch: { data: researchData.status === 'fulfilled' ? researchData.value : [], loading: false },
        legislativeDrafting: { data: draftingData.status === 'fulfilled' ? draftingData.value : [], loading: false },
        legalAdvisory: { data: advisoryData.status === 'fulfilled' ? advisoryData.value : [], loading: false },
        sectorDirectives: { data: sectorData.status === 'fulfilled' ? sectorData.value : [], loading: false },
        registerDirectives: { data: registerData.status === 'fulfilled' ? registerData.value : [], loading: false },
        legalTraining: { data: trainingData.status === 'fulfilled' ? trainingData.value : [], loading: false }
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
      showToast(t("ErrorLoadingData") || "Error loading data", "error");
    }
  }, [showToast, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply filters
  const filteredData = useMemo(() => {
    const { dateRange, startDate, endDate, services, status, search } = filters;
    const { start, end } = getDateRange(dateRange, startDate, endDate);

    const result: Record<ServiceKey, ServiceData<any>> = {
      legalResearch: { data: [], loading: false },
      legislativeDrafting: { data: [], loading: false },
      legalAdvisory: { data: [], loading: false },
      sectorDirectives: { data: [], loading: false },
      registerDirectives: { data: [], loading: false },
      legalTraining: { data: [], loading: false }
    };

    SERVICE_CONFIGS.forEach(config => {
      const serviceKey = config.key;
      if (services.length > 0 && !services.includes(serviceKey)) {
        result[serviceKey] = { ...allServiceData[serviceKey], data: [] };
        return;
      }

      let filtered = filterByDate(allServiceData[serviceKey].data, start, end);
      filtered = filterBySearch(filtered, search, ['title', 'topic', 'reference_number', 'request_summary']);

      if (status !== 'all') {
        const statusMap = getStatusMap(config.key);
        filtered = filtered.filter(item => {
          const statusValue = item.status || item.current_status || item.request_status;
          const statusDef = Object.values(statusMap).find(s => s.value === statusValue);
          if (!statusDef) return false;
          const categorized = categorizeStatus(statusDef.label);
          return categorized === status;
        });
      }

      result[serviceKey] = { ...allServiceData[serviceKey], data: filtered };
    });

    return result;
  }, [allServiceData, filters]);

  // Calculate aggregated stats
  const aggregateStats = useMemo(() => {
    let grandTotal = 0;
    let active = 0, completed = 0, pendingReview = 0;

    SERVICE_CONFIGS.forEach(config => {
      const data = filteredData[config.key].data;
      grandTotal += data.length;
      const statusMap = getStatusMap(config.key);
      const stats = calculateStats(data, statusMap);
      active += stats.active;
      completed += stats.completed;
      pendingReview += stats.pendingReview;
    });

    return { grandTotal, active, completed, pendingReview };
  }, [filteredData]);

  // Calculate completion rate
  const completionRate = aggregateStats.grandTotal > 0 
    ? Math.round((aggregateStats.completed / aggregateStats.grandTotal) * 100) 
    : 0;

  // Calculate productivity score
  const productivityScore = aggregateStats.grandTotal > 0 
    ? Math.round((aggregateStats.completed / aggregateStats.grandTotal) * 100) 
    : 0;

  // Calculate changes (mock for now - comparing to previous period)
  const changes = useMemo(() => ({
    total: '+12%',
    active: '+8%',
    completed: '+15%',
    pendingReview: '-5%'
  }), []);

  // Status counts per service
  const statusCounts = useMemo(() => {
    const counts: Record<ServiceKey, Record<string, number>> = {
      legalResearch: { active: 0, completed: 0, pendingReview: 0 },
      legislativeDrafting: { active: 0, completed: 0, pendingReview: 0 },
      legalAdvisory: { active: 0, completed: 0, pendingReview: 0 },
      sectorDirectives: { active: 0, completed: 0, pendingReview: 0 },
      registerDirectives: { active: 0, completed: 0, pendingReview: 0 },
      legalTraining: { active: 0, completed: 0, pendingReview: 0 }
    };

    SERVICE_CONFIGS.forEach(config => {
      const statusMap = getStatusMap(config.key);
      counts[config.key] = calculateStats(filteredData[config.key].data, statusMap);
    });

    return counts;
  }, [filteredData]);

  // Get matched service count for filter button label
  const matchedServiceCount = useMemo(() =>
    filters.services.length > 0 ? filters.services.length : SERVICE_CONFIGS.length,
    [filters.services]
  );

  // Stats cards
  const statsCards: StatsCard[] = [
    {
      title: t("TotalRequests"),
      value: aggregateStats.grandTotal,
      change: changes.total,
      trend: 'up',
      icon: <Briefcase className="w-5 h-5" />,
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600'
    },
    {
      title: t("ActiveTasks"),
      value: aggregateStats.active,
      change: changes.active,
      trend: 'up',
      icon: <Activity className="w-5 h-5" />,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600'
    },
    {
      title: t("Completed"),
      value: aggregateStats.completed,
      change: changes.completed,
      trend: 'up',
      icon: <CheckCircle className="w-5 h-5" />,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    },
    {
      title: t("PendingReview"),
      value: aggregateStats.pendingReview,
      change: changes.pendingReview,
      trend: 'down',
      icon: <AlertCircle className="w-5 h-5" />,
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600'
    }
  ];

  // Handlers
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData().finally(() => setIsRefreshing(false));
  };

  const handleExport = () => {
    exportToCSV(filteredData, statusCounts, t);
    showToast("Data exported successfully", "success");
  };

  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleService = (service: ServiceKey) => {
    setFilters(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const clearFilters = () => {
    setFilters({
      dateRange: 'month',
      startDate: '',
      endDate: '',
      services: [],
      status: 'all',
      search: ''
    });
  };

  const hasActiveFilters = filters.services.length > 0 || filters.status !== 'all' || filters.dateRange !== 'month' || filters.search;

  // Recent activity data (mock - replace with actual data)
  const recentActivities = [
    { id: 1, type: 'legalResearch', action: 'New research request submitted', time: '2 hours ago', status: 'pending' },
    { id: 2, type: 'legalAdvisory', action: 'Advisory opinion completed', time: '5 hours ago', status: 'completed' },
    { id: 3, type: 'legislativeDrafting', action: 'Draft legislation under review', time: '1 day ago', status: 'active' },
    { id: 4, type: 'legalTraining', action: 'Training program scheduled', time: '2 days ago', status: 'active' },
    { id: 5, type: 'sectorDirectives', action: 'Sector directive verified', time: '3 days ago', status: 'completed' }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Full width header with background */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <PageTitle
            text={t("LegalResearchCoordinatorDashboard") || "Legal Research Coordinator Dashboard"}
            icon={<Home />}
            iconBgColor="bg-slate-800"
            iconColor="text-white"
            subtitle={t("LegalResearchCoordinatorDashboardSubtitle") || "Overview of all legal research, drafting, advisory, and related services"}
          />
        </div>
      </div>

      {/* Main Content - Full Width */}
      <div className="px-6 py-6">
        {/* Filter Bar */}
        <div className="bg-white rounded-lg border border-slate-200 mb-6">
          <div className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  leftIcon={<Filter className="w-4 h-4" />}
                  className="border-slate-200 hover:bg-slate-50"
                >
                  {t("FilterDashboard")}
                  {hasActiveFilters && (
                    <span className="ml-1.5 bg-slate-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {filters.services.length + (filters.status !== 'all' ? 1 : 0) + (filters.dateRange !== 'month' ? 1 : 0) + (filters.search ? 1 : 0)}
                    </span>
                  )}
                </Button>

                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-600">
                    <X className="w-4 h-4 mr-1" />
                    {t("ClearFilters")}
                  </Button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  loading={isRefreshing}
                  leftIcon={<RefreshCw className="w-4 h-4" />}
                  className="border-slate-200 hover:bg-slate-50"
                >
                  {t("Refresh")}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleExport}
                  leftIcon={<Download className="w-4 h-4" />}
                  className="bg-slate-800 hover:bg-slate-900"
                >
                  {t("ExportCSV")}
                </Button>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                {/* Date Range */}
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {t("DateRange")}
                    </label>
                    <div className="flex gap-2">
                      {(['today', 'week', 'month', 'year'] as const).map(range => (
                        <button
                          key={range}
                          onClick={() => handleFilterChange('dateRange', range)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            filters.dateRange === range
                              ? 'bg-slate-800 text-white border-slate-800'
                              : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {t(range.charAt(0).toUpperCase() + range.slice(1))}
                        </button>
                      ))}
                    </div>
                  </div>

                  {filters.dateRange === 'custom' && (
                    <div className="flex items-center gap-2">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          {t("StartDate")}
                        </label>
                        <input
                          type="date"
                          value={filters.startDate}
                          onChange={(e) => handleFilterChange('startDate', e.target.value)}
                          className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                          {t("EndDate")}
                        </label>
                        <input
                          type="date"
                          value={filters.endDate}
                          onChange={(e) => handleFilterChange('endDate', e.target.value)}
                          className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Services Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("Services")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleFilterChange('services', [])}
                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                        filters.services.length === 0
                          ? 'bg-slate-800 text-white border-slate-800'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {t("AllServices")}
                    </button>
                    {SERVICE_CONFIGS.map(config => (
                      <button
                        key={config.key}
                        onClick={() => toggleService(config.key)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                          filters.services.includes(config.key)
                            ? 'bg-slate-800 text-white border-slate-800'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {t(config.title)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("Status")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {(['all', 'active', 'completed', 'pendingReview'] as const).map(status => (
                      <button
                        key={status}
                        onClick={() => handleFilterChange('status', status)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                          filters.status === status
                            ? 'bg-slate-800 text-white border-slate-800'
                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {status === 'all' ? t("AllStatus") : t(status.charAt(0).toUpperCase() + status.slice(1))}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Search */}
                <div className="max-w-md">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t("Search")}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder={t("SearchPlaceholder")}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-800"
                    />
                    {filters.search && (
                      <button
                        onClick={() => handleFilterChange('search', '')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
          {statsCards.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-slate-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {stat.value.toLocaleString()}
                  </p>
                  <p className={`text-sm mt-2 flex items-center gap-1 ${
                    stat.trend === 'up' ? 'text-emerald-600' :
                    stat.trend === 'down' ? 'text-rose-600' :
                    'text-slate-500'
                  }`}>
                    <TrendingUp className="w-3 h-3" />
                    {stat.change} {t("ChangeFromLastMonth", { change: stat.change })}
                  </p>
                </div>
                <div className={`p-2.5 ${stat.iconBg} rounded-lg`}>
                  <span className={stat.iconColor}>{stat.icon}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Results Summary */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between text-sm text-slate-600 mb-6">
            <p>
              {t("Showing")} <span className="font-semibold text-slate-900">{aggregateStats.grandTotal.toLocaleString()}</span> {t("of")}{' '}
              <span className="font-semibold text-slate-900">
                {Object.values(allServiceData).reduce((acc, svc) => acc + svc.data.length, 0).toLocaleString()}
              </span> {t("requests")}
            </p>
          </div>
        )}

        {/* Main Content Area: Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column: Services Grid (3/4 width) */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {SERVICE_CONFIGS.map(config => {
                const service = filteredData[config.key];
                const total = service.data.length;
                const stats = statusCounts[config.key] || { active: 0, completed: 0, pendingReview: 0 };
                const isLoading = service.loading;
                const completionPercent = total > 0 ? Math.round((stats.completed / total) * 100) : 0;

                return (
                  <div
                    key={config.key}
                    className="group bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200 overflow-hidden"
                  >
                    <div className="p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`p-2.5 ${config.bgColor} rounded-lg`}>
                          <span className={config.iconColor}>{config.icon}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900 text-sm">
                            {t(config.title)}
                          </h4>
                          <p className="text-slate-500 text-xs mt-0.5">
                            {t(config.description)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-end justify-between mb-3">
                        <div>
                          <span className="text-3xl font-bold text-slate-900">
                            {isLoading ? '—' : total.toLocaleString()}
                          </span>
                          <span className="text-sm text-slate-500 ml-1.5">
                            {config.key === 'legalTraining' ? t("programs") : t("requests")}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-slate-500">Progress</span>
                          <p className="text-base font-semibold text-slate-700">{completionPercent}%</p>
                        </div>
                      </div>

                      <div className="w-full bg-slate-100 rounded-full h-1.5 mb-4">
                        <div 
                          className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                          style={{ width: `${completionPercent}%` }}
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-3 pt-2">
                        <div className="text-center">
                          <p className="text-xs text-slate-500 mb-1">Active</p>
                          <p className="text-lg font-semibold text-slate-900">
                            {isLoading ? '—' : stats.active}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500 mb-1">Completed</p>
                          <p className="text-lg font-semibold text-emerald-600">
                            {isLoading ? '—' : stats.completed}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500 mb-1">Review</p>
                          <p className="text-lg font-semibold text-rose-600">
                            {isLoading ? '—' : stats.pendingReview}
                          </p>
                        </div>
                      </div>

                      {!isLoading && total > 0 && (stats.pendingReview > 0 || stats.active > 0) && (
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                          {stats.active > 0 && (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 ${config.bgColor} rounded-md text-xs font-medium ${config.iconColor}`}>
                              <Clock className="w-3 h-3" />
                              {stats.active} in progress
                            </span>
                          )}
                          {stats.pendingReview > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-rose-50 text-rose-600 rounded-md text-xs font-medium">
                              <AlertCircle className="w-3 h-3" />
                              {stats.pendingReview} for review
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="px-5 py-3 bg-slate-50 border-t border-slate-100">
                      <button className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
                        View Details
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: Performance & Analytics (1/4 width) */}
          <div className="lg:col-span-1 space-y-6">
            {/* Welcome Card */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-lg p-5 text-white">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-white/10 rounded-lg">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                {/* <div>
                  <p className="text-xs text-white/70">Welcome back,</p>
                  <p className="text-sm font-semibold text-white">{user?.name || 'Coordinator'}</p>
                </div> */}
              </div>
              <p className="text-xs text-white/60">{currentDate}</p>
            </div>

            {/* Performance Score Circle */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 text-center">
              <div className="relative inline-flex items-center justify-center mb-3">
                <svg className="w-28 h-28 transform -rotate-90">
                  <circle
                    cx="56"
                    cy="56"
                    r="52"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-slate-100"
                  />
                  <circle
                    cx="56"
                    cy="56"
                    r="52"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 52}`}
                    strokeDashoffset={`${2 * Math.PI * 52 * (1 - productivityScore / 100)}`}
                    className="text-emerald-500 transition-all duration-1000"
                  />
                </svg>
                <div className="absolute">
                  <Star className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-slate-900">{productivityScore}%</p>
                </div>
              </div>
              <p className="text-sm font-medium text-slate-900">Productivity Score</p>
              <p className="text-xs text-slate-500 mt-1">Based on completed tasks</p>
            </div>

            {/* Performance Overview */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Performance Overview</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">Completion Rate</span>
                    <span className="font-medium text-slate-900">{completionRate}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div 
                      className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${completionRate}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Activity className="w-3 h-3 text-amber-500" />
                      <span className="text-xs text-slate-600">Active Tasks</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">
                      {aggregateStats.active.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-emerald-500" />
                      <span className="text-xs text-slate-600">Completed</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">
                      {aggregateStats.completed.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-3 h-3 text-rose-500" />
                      <span className="text-xs text-slate-600">Needs Review</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-900">
                      {aggregateStats.pendingReview.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-slate-100 rounded-lg">
                    <Clock className="w-4 h-4 text-slate-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">Recent Activity</h3>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                      activity.status === 'completed' ? 'bg-emerald-50' : 
                      activity.status === 'pending' ? 'bg-rose-50' : 'bg-amber-50'
                    }`}>
                      {activity.status === 'completed' ? (
                        <CheckCircle className="w-3 h-3 text-emerald-600" />
                      ) : activity.status === 'pending' ? (
                        <AlertCircle className="w-3 h-3 text-rose-600" />
                      ) : (
                        <Clock className="w-3 h-3 text-amber-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">{activity.action}</p>
                      <p className="text-xs text-slate-500">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="px-5 py-4 border-b border-slate-100">
                <h3 className="text-sm font-semibold text-slate-900">Quick Actions</h3>
              </div>
              <div className="p-3 space-y-1">
                <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  + New Legal Research Request
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  + Create Legislative Draft
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  + Request Legal Advisory
                </button>
                <button className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors">
                  + Schedule Training Session
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalResearchCoordinatorDashboard;